import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Confetti } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { ChevronLeft, Reset } from '@factorialco/f0-react/icons/app'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useTrainingContentExitLogger } from 'modules/trainings/hooks/useTrainingContentExitLogger'

interface ConsumerCompletedStateProps {
  onRestart?: () => void
  isPreviewMode?: boolean
  onClosePreview?: () => void
}

export const ConsumerCompletedState = ({
  onRestart,
  isPreviewMode = false,
  onClosePreview,
}: ConsumerCompletedStateProps) => {
  const { trainingId } = useParams<{ trainingId: string }>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showConfetti, setShowConfetti] = useState(true)
  const hasLoggedExitOnCompletion = useRef(false)

  const { logExit } = useTrainingContentExitLogger({
    trainingId,
  })

  // Log exit once when user reaches the completed state
  useEffect(() => {
    if (!hasLoggedExitOnCompletion.current) {
      hasLoggedExitOnCompletion.current = true
      void logExit()
    }
  }, [logExit])

  const handleBack = async () => {
    if (isPreviewMode && onClosePreview) {
      onClosePreview()
      return
    }

    navigate(
      navigation.myTrainings.revamp.detailsRevamp.view.contentOverview.fullPath({
        trainingId: trainingId ?? '',
      })
    )
  }

  const actions = [
    {
      label: i18n.t('trainings.content.consumer.empty_state.back'),
      onClick: handleBack,
      variant: 'outline' as const,
      icon: ChevronLeft,
    },
    ...(onRestart
      ? [
          {
            label: i18n.t('trainings.content.consumer.empty_state.start_from_beginning'),
            onClick: onRestart,
            variant: 'default' as const,
            icon: Reset,
          },
        ]
      : []),
  ]

  return (
    <>
      {showConfetti && (
        <Box
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <Confetti onEnd={() => setShowConfetti(false)} />
          <Confetti onEnd={() => setShowConfetti(false)} />
        </Box>
      )}

      <OneEmptyState
        actions={actions}
        title={i18n.t('trainings.content.consumer.empty_state.title')}
        description={i18n.t('trainings.content.consumer.empty_state.description')}
        emoji="🎉"
      />
    </>
  )
}
